/**
 * The typst metadata stream, and what it becomes.
 *
 * Pure: no filesystem, no database, no typst. Everything here is a function of the `<gradias>` values
 * `typst eval` returns (see typst/exam.typ), which is what makes the structure contract testable without
 * a compile — feed it a recorded stream and the task tree is deterministic.
 */

import { BoundingBox } from '../../BoundingBox.js';
import { TaskType } from '../task-types/task-main.js';

export interface BlockPosition {
  page: number;
  /** Points from the page's top-left corner. */
  x: number;
  y: number;
}

/**
 * What kind of answer a task asks for — decided when the exam is written, not inferred afterwards.
 *
 * It drives two things at once, which is why it is one vocabulary and not two: what the printed sheet puts
 * under the task (ruled lines, squared paper, an empty box, nothing), and which input the online version
 * of the exam shows the student. `ANSWER-TYPES` in typst/exam.typ is the same list; that file documents
 * what each one is for.
 */
export type AnswerType = 'lines' | 'numbered-lines' | 'grid' | 'blank' | 'inline' | 'none';

/**
 * `mc`/`match`/`grid-paper`/`prompt-list`/`gap-text` — the answer-bearing widgets a task's `inline` answer
 * can be. Their own `id` is an internal bookkeeping detail that exam.typ assigns itself: which TASK a
 * widget belongs to is never read off that id, only off where it falls in the stream — see
 * `computeBoundingBoxes`'s nesting pass.
 *
 * `gap` (singular, one blank) is deliberately NOT in this set: a `#gap` is a scattered point mark with no
 * id/box of its own, unlike `#gap-text`, the wrapper AROUND every `#gap` in a passage that does have one
 * (see `AnswerRegion.gapBoxes`'s own comment for the older sources that predate that wrapper and have
 * neither).
 */
export const INLINE_ANSWER_WIDGET_KINDS = new Set(['mc', 'match', 'grid-paper', 'prompt-list', 'gap-text']);

/** One entry of the `<gradias>` metadata stream emitted by typst/exam.typ. */
export interface GradiasBlock {
  kind:
    | 'exam'
    | 'header'
    | 'task'
    | 'material'
    | 'figure'
    | 'figure-image'
    | 'figure-callout'
    | 'mc'
    | 'mc-option'
    | 'match'
    | 'match-row'
    | 'grid-paper'
    | 'gap-text'
    | 'gap'
    | 'prompt-list'
    | 'prompt-row'
    | 'answer'
    | 'answer-visible'
    | 'end';
  at?: 'start' | 'end' | 'point';
  id?: string;
  pos?: BlockPosition;
  /**
   * Present on every boxed block's own `"start"` mark — typst measures its own rendered content (see
   * `gradias-track` in exam.typ) and reports the exact box directly, so nothing downstream computes
   * anything from a paired end-mark position. Points from the page's top-left corner, same as `pos`.
   */
  width?: number;
  height?: number;

  // answer region — see `answer-region` in exam.typ. `task` is the number of the task it declares an
  // answer for. For `inline`, the WIDGET that actually carries the answer is found by nesting, not by any
  // id relationship to this mark — see `computeBoundingBoxes`.
  answerType?: AnswerType;
  task?: string;

  // task
  number?: string;
  taskType?: string;
  points?: number | null;
  ownPoints?: number | null;
  depth?: number;
  /** `answer` only — how many ruled lines were printed, for the line types. */
  lines?: number | null;
  text?: string;

  // multiple choice — the canonical Markdown checklist, one option per entry
  options?: string[];
  /**
   * 0-based position within the widget that emitted this mark — an option within its `#mc`, a callout
   * within its `#figure-block`, a row within its `#match-columns`, a blank within its `#gap`/`#prompt-list`
   * (`gap`'s own `index` is the author-facing gap NUMBER, 1-based — see `AnswerRegion.gapBoxes`, which
   * sorts back into that order rather than trusting document order).
   */
  index?: number;

  /**
   * `match-row` only — the connector lane's own width, in points. The mark's `pos` gives the lane's left
   * edge and the row's top; this turns that into both of its ends (see `AnswerRegion.matchRows`).
   */
  gapWidthPt?: number;
  /** `match-row` only — whether this row actually has an item on that side; the columns can differ in length. */
  hasLeft?: boolean;
  hasRight?: boolean;
  /**
   * `match-row` — one line of body text, in points, measured by typst itself.
   * `prompt-row` — the blank cell's own box height, in points, measured the same way (see `widthPt` right
   * below for its width; together with `pos` they give the whole box, not just a lane/top like `match-row`
   * needs).
   */
  lineHeightPt?: number;
  /**
   * `prompt-row` only — the blank cell's own width, in points. The mark's `pos` gives the box's top-left;
   * this and `lineHeightPt` turn that into the blank's full extent (see `AnswerRegion.promptRows`). The
   * width is the same value on every row — a `1fr` grid column is exactly as wide as whatever `auto`
   * neighbours it leave, uniformly for the whole `#prompt-list`, not per row.
   */
  widthPt?: number;

  // figure / material
  ref?: string | null;
  caption?: string | null;
  title?: string | null;
  path?: string | null;
  /** material only — every task number (as declared, not normalized) that depends on this material. */
  tasks?: string[] | null;
  /**
   * figure-image only — a labelling figure's actual rendered image rect is narrower than its own
   * figure-block box once callout number-boxes add gutters on either side (imageXPt/imageWidthPt, in
   * points from the figure-block's own left edge), and shorter than it once there's a caption
   * (imageHeightPt) — the figure-block's own box runs to the end of the caption text, not the picture.
   * y is the figure's own, unchanged: the picture is the first thing a figure-block renders.
   */
  imageXPt?: number;
  imageWidthPt?: number;
  imageHeightPt?: number;
  /**
   * `figure-callout` only — one numbered answer box's own rect, in points from the figure-block's own
   * top-left (the same anchor `imageXPt` uses). Emitted per callout by `callout-mark` in exam.typ.
   */
  boxXPt?: number;
  boxYPt?: number;
  boxWidthPt?: number;
  boxHeightPt?: number;

  // exam header
  version?: number;
  subject?: string | null;
  class?: string | null;
  date?: string | null;
  teacher?: string | null;
  school?: string | null;
  duration?: string | null;
  declaredTotalPoints?: number | null;
  lang?: string;
  /** exam header only — the physical page, in points, so a start/end pt-position pair can become a box. */
  pageWidthPt?: number;
  pageHeightPt?: number;
  contentWidthPt?: number;
  contentHeightPt?: number;
}

/** A block's on-page extent, in points from the page's top-left corner — one per task/material id. */
export interface BlockBoundingBox {
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Every boxed block's `"start"` mark already carries its own exact box — typst measured its own rendered
 * content (see `gradias-track` in exam.typ) rather than this reading two marks' positions and subtracting
 * them, so there is nothing to compute here beyond copying the fields over. `gradias-track`'s body is
 * always `breakable: false`, which is what makes the measurement trustworthy: the content cannot actually
 * split across a page break, so there is no "clipped to the page's bottom edge" case to approximate either.
 */
/**
 * Where a task's answer goes, and what kind of answer it is — keyed by task number (as declared).
 *
 * Separate from `boxes` because a task's own box spans its prompt AND its answer together: it is the right
 * hitbox for "click this task", and useless for "put the input field here". This is what the online exam
 * page positions its widgets against.
 */
export interface AnswerRegion {
  taskNumber: string;
  answerType: AnswerType;
  /**
   * Absent only for `none` — nothing on the page answers that task (except a labelling figure, whose own
   * boxes are `calloutBoxes` below). Every other kind has one: a printed region is tracked directly, and an
   * `inline` one takes the box of the widget nested inside its task, which `validateBlocks` requires to
   * exist.
   */
  box?: BlockBoundingBox;
  /** How many ruled lines were printed. Line types only; a box's size is a length, not a count. */
  lines?: number;
  /**
   * `choice` (a nested `#mc`) only — one entry per option, in order, dividing `box` the way it is ACTUALLY
   * printed rather than assuming every row is the same height: a wrapped option's row is genuinely taller,
   * and typst already knows exactly where each row starts (see the `mc-option` marks in exam.typ's `#mc`)
   * without anything here having to measure text wrapping itself. It is also what says how many options
   * there ARE: the online page draws one checkbox per entry rather than per parsed description line.
   */
  optionRows?: { y: number; height: number }[];
  /**
   * A LABELLING FIGURE'S OWN ANSWER FIELDS — the numbered boxes `#figure-block` draws beside the picture
   * and connects to it by leader lines, one per callout, in callout order. On paper the student writes the
   * structure's name INTO one of these boxes; this is what lets the online page put the input in the same
   * place instead of on a separate list of ruled lines.
   *
   * Absent unless the task's body holds a figure with callouts (the usual LABEL_IMAGE shape — the
   * contract in exam-creation-agent.ts puts the figure inside the task that refers to it). A labelling
   * figure that lives in a shared `#material` block instead has no single owning task, so it resolves to
   * none here and that task falls back to its printed answer region, whatever it is.
   *
   * Full boxes rather than just positions, because each one is an input's exact size as well as its spot.
   */
  calloutBoxes?: BlockBoundingBox[];
  /**
   * `mapping` (a nested `#match-columns`) only — where each row's connector can be drawn from and to,
   * absolute in the page's own points.
   *
   * `x`/`rightX` are the empty lane between the two printed columns (identical for every row, carried
   * per-row only so a reader needs nothing else) and `y` is the middle of that row's FIRST line, where its
   * "1." and "A)" are printed. Both ends stay in that lane, aligned in two straight columns — the lane is
   * sized to leave a connector worth drawing (see `gap-width` in exam.typ), so there is nothing to gain by
   * chasing each row's own text and a legible column of handles to lose.
   *
   * `hasLeft`/`hasRight` say whether this row has an item on that side at all — the two columns can be
   * different lengths, and a row past the end of one of them has nothing there to connect.
   *
   * Needed because none of it is derivable from the widget's box: the two text columns are `1fr` each, so
   * how wide they actually rendered is known only inside typst's own layout. This is also what says how
   * many rows there are, so the online page draws handles per printed row rather than per parsed line.
   */
  matchRows?: { y: number; x: number; rightX: number; hasLeft: boolean; hasRight: boolean }[];
  /**
   * `GAP_TEXT` (a task body scattered with `#gap` calls) only — one box per gap, in gap-NUMBER order (not
   * necessarily document order — an author could in principle write `#gap(2)` before `#gap(1)`), each the
   * small blank rect the student fills in directly in the flowing text.
   *
   * ALWAYS present alongside `box` for a passage wrapped in `#gap-text(...)` (every current source; see
   * exam.typ) — `box` is that wrapper's own tracked rect (the passage alone, not any instructions/word bank
   * above it), and this is still needed for the per-blank online input fields, which `box` alone cannot
   * place (a `1fr` grid column's rendered width is knowable, a point mid-paragraph is not).
   *
   * Only field present for an OLDER source with bare `#gap(...)` calls and no wrapper — no `box` of its own
   * the way `mc`/`match`/`grid-paper`/`prompt-list` have, so a GAP_TEXT task from before the wrapper existed
   * resolves through this alone: see `computeBoundingBoxes`'s nesting pass and `positioned` in
   * student-exam/page.tsx, both of which already treat `calloutBoxes` the same way for the same reason.
   */
  gapBoxes?: BlockBoundingBox[];
  /**
   * `TRANSLATION` (a nested `#prompt-list`) only — the blank cell at the end of each printed row, one per
   * item, in order. `box` above is the WHOLE widget (every row together, needed to resolve `inline` at
   * all — see `INLINE_ANSWER_WIDGET_KINDS`); this is where each row's own answer field goes.
   *
   * Needed for the same reason `matchRows` is: the blank is a `1fr` grid column, whose rendered width is
   * knowable only inside typst's own layout, never from `box` alone.
   */
  promptRows?: BlockBoundingBox[];
}

export interface ExamLayout {
  /** The full page size, in points — the coordinate space every box below is expressed in. */
  pageWidthPt: number;
  pageHeightPt: number;
  boxes: Record<string, BlockBoundingBox>;
  /**
   * Optional because an exam compiled before answer regions existed has none — a reader must treat an
   * absent map as "unknown", not as "this exam has no answers".
   */
  answers?: Record<string, AnswerRegion>;
}

export function computeBoundingBoxes(blocks: GradiasBlock[]): ExamLayout {
  const examHeader = blocks.find(block => block.kind === 'exam' && block.at === 'start');

  const boxes: Record<string, BlockBoundingBox> = {};
  for (const block of blocks) {
    if (block.at !== 'start' || !block.id || !block.pos) continue;
    if (typeof block.width !== 'number' || typeof block.height !== 'number') continue;
    boxes[block.id] = { page: block.pos.page, x: block.pos.x, y: block.pos.y, width: block.width, height: block.height };
  }

  // A second pass, not folded into the one above: a `figure-image` point mark is emitted WHILE its figure
  // is still open (between that figure's own start and end), so `boxes[id]` for the figure itself does not
  // exist yet the first time this loop would reach it.
  for (const block of blocks) {
    if (block.kind !== 'figure-image' || !block.id) continue;
    const figureBox = boxes[block.id];
    if (!figureBox) continue;
    if (typeof block.imageXPt !== 'number' || typeof block.imageWidthPt !== 'number') continue;
    if (typeof block.imageHeightPt !== 'number') continue;
    boxes[`${block.id}-image`] = {
      page: figureBox.page,
      x: figureBox.x + block.imageXPt,
      y: figureBox.y,
      width: block.imageWidthPt,
      height: block.imageHeightPt,
    };
  }

  // Each labelling figure's numbered answer boxes, keyed by the FIGURE's id — the nesting pass below
  // resolves which task owns them. Same shape and the same reason as `-image` right above: the offsets are
  // from the figure-block's own top-left, so this needs `boxes[figureId]` to already exist.
  const calloutBoxesByFigure: Record<string, BlockBoundingBox[]> = {};
  const calloutMarksByFigure = new Map<string, { index: number; box: BlockBoundingBox }[]>();
  for (const block of blocks) {
    if (block.kind !== 'figure-callout' || !block.id || typeof block.index !== 'number') continue;
    const figureBox = boxes[block.id];
    if (!figureBox) continue;
    if (typeof block.boxXPt !== 'number' || typeof block.boxYPt !== 'number') continue;
    if (typeof block.boxWidthPt !== 'number' || typeof block.boxHeightPt !== 'number') continue;
    const marks = calloutMarksByFigure.get(block.id) ?? [];
    marks.push({
      index: block.index,
      box: {
        page: figureBox.page,
        x: figureBox.x + block.boxXPt,
        y: figureBox.y + block.boxYPt,
        width: block.boxWidthPt,
        height: block.boxHeightPt,
      },
    });
    calloutMarksByFigure.set(block.id, marks);
  }
  // Back into callout order: the marks are emitted column by column (all the left-hand boxes, then all the
  // right-hand ones), and it is the callout's own number that a master solution pairs on.
  for (const [figureId, marks] of calloutMarksByFigure) {
    calloutBoxesByFigure[figureId] = marks
      .slice()
      .sort((a, b) => a.index - b.index)
      .map(mark => mark.box);
  }

  // Each `#match-columns`'s connector endpoints, keyed by ITS id — same deal, resolved to a task below.
  type MatchRow = NonNullable<AnswerRegion['matchRows']>[number];
  const matchRowsByWidget: Record<string, MatchRow[]> = {};
  const matchMarksById = new Map<string, { index: number; row: MatchRow }[]>();
  for (const block of blocks) {
    if (block.kind !== 'match-row' || !block.id || !block.pos || typeof block.index !== 'number') continue;
    if (typeof block.gapWidthPt !== 'number' || typeof block.lineHeightPt !== 'number') continue;
    const marks = matchMarksById.get(block.id) ?? [];
    marks.push({
      index: block.index,
      row: {
        // The mark sits at the row's top; the connector leaves from the middle of its first line.
        y: block.pos.y + block.lineHeightPt / 2,
        x: block.pos.x,
        rightX: block.pos.x + block.gapWidthPt,
        hasLeft: block.hasLeft !== false,
        hasRight: block.hasRight !== false,
      },
    });
    matchMarksById.set(block.id, marks);
  }
  for (const [id, marks] of matchMarksById) {
    matchRowsByWidget[id] = marks
      .slice()
      .sort((a, b) => a.index - b.index)
      .map(mark => mark.row);
  }

  // A third pass, for the same reason the second one exists: a boxed answer region's box only exists in
  // `boxes` once the first pass above has run.
  const answers: Record<string, AnswerRegion> = {};
  for (const block of blocks) {
    if (block.kind !== 'answer') continue;
    const taskNumber = String(block.task ?? '').trim();
    if (!taskNumber) continue;
    const answerType = (block.answerType ?? 'lines') as AnswerType;
    // `inline` and `none` are POINT marks with no box of their own, so there is nothing to look up for
    // them here — an `inline` task's box comes from the widget nested in its body, in the pass below.
    // Skipping them is not just tidiness: `answer-region` names its mark "<task id>-answer", so a widget a
    // writer happened to give that same name would be found here BY NAME, out of its task's body and
    // without the per-row geometry the nesting pass attaches.
    //
    // The "-visible" box (see `answer-space` in exam.typ) is the tight one — just the dashed rectangle,
    // not the breathing room typst prints around it — so it wins whenever present. `answer-lines` prints
    // no such mark, and falls through to its own box.
    const boxedId = answerType === 'inline' || answerType === 'none' ? undefined : block.id;
    const box = boxedId ? (boxes[`${boxedId}-visible`] ?? boxes[boxedId]) : undefined;
    answers[taskNumber] = {
      taskNumber,
      answerType,
      ...(box ? { box } : {}),
      ...(Number(block.lines) > 0 ? { lines: Number(block.lines) } : {}),
    };
  }

  // Every `#mc`'s own option rows, keyed by ITS id (not the task's number — the nesting pass right below
  // resolves that): each mark fires at that row's own top edge, so consecutive marks bound a row exactly
  // as tall as it actually printed. The last row runs to the mc's own bottom edge (`boxes[id]`), which is
  // why this reads AFTER the first pass rather than being folded into it.
  const mcOptionRows: Record<string, { y: number; height: number }[]> = {};
  const mcOptionMarksById = new Map<string, { index: number; y: number }[]>();
  for (const block of blocks) {
    if (block.kind !== 'mc-option' || !block.id || !block.pos || typeof block.index !== 'number') continue;
    const marks = mcOptionMarksById.get(block.id) ?? [];
    marks.push({ index: block.index, y: block.pos.y });
    mcOptionMarksById.set(block.id, marks);
  }
  for (const [id, marks] of mcOptionMarksById) {
    const mcBox = boxes[id];
    if (!mcBox) continue;
    const sorted = marks.slice().sort((a, b) => a.index - b.index);
    mcOptionRows[id] = sorted.map((mark, i) => {
      const nextY = i + 1 < sorted.length ? sorted[i + 1]!.y : mcBox.y + mcBox.height;
      return { y: mark.y, height: Math.max(0, nextY - mark.y) };
    });
  }

  // Every `#prompt-list`'s own blank cells, keyed by ITS id — unlike `mcOptionRows` above, the mark
  // carries its own exact box directly (`pos` + `widthPt` + `lineHeightPt`), the same way `boxes` itself
  // does for a `gradias-track`'d block: the row's blank cell is bottom-aligned within its grid row (see
  // `prompt-list` in exam.typ), so there is no well-defined "next row's top" to bound this one by — only
  // the box's own reported size is trustworthy.
  const promptRowsByWidget: Record<string, BlockBoundingBox[]> = {};
  const promptRowMarksById = new Map<string, { index: number; box: BlockBoundingBox }[]>();
  for (const block of blocks) {
    if (block.kind !== 'prompt-row' || !block.id || !block.pos || typeof block.index !== 'number') continue;
    if (typeof block.widthPt !== 'number' || typeof block.lineHeightPt !== 'number') continue;
    const marks = promptRowMarksById.get(block.id) ?? [];
    marks.push({
      index: block.index,
      box: { page: block.pos.page, x: block.pos.x, y: block.pos.y, width: block.widthPt, height: block.lineHeightPt },
    });
    promptRowMarksById.set(block.id, marks);
  }
  for (const [id, marks] of promptRowMarksById) {
    promptRowsByWidget[id] = marks
      .slice()
      .sort((a, b) => a.index - b.index)
      .map(mark => mark.box);
  }

  // A fourth pass: which task an `inline` answer's widget (#mc/#match-columns/#grid-paper/#prompt-list,
  // plus #gap, resolved separately below) belongs to, by
  // NESTING — whichever one of these falls between a task's own start and end is that task's answer,
  // regardless of what its own id happens to be (auto-generated by exam.typ, or an explicit override on an
  // older source) — see INLINE_ANSWER_WIDGET_KINDS. Subtasks are sequential siblings, never lexically
  // nested inside one another, so at most one task is ever open at a time.
  const taskIds = new Set(blocks.filter(block => block.kind === 'task' && block.at === 'start').map(block => block.id ?? ''));
  // Gaps a GAP_TEXT task's `#gap` calls scatter through its own prose, grouped by whichever task is open
  // when each is reached (the same nesting this whole pass resolves everything else by) and sorted back
  // into printed gap-NUMBER order once collection finishes, since document order and a gap's own `number`
  // argument are normally the same but not guaranteed to be.
  const gapMarksByTask = new Map<string, { index: number; box: BlockBoundingBox }[]>();
  let openTask: { id: string; number: string } | undefined;
  for (const block of blocks) {
    if (block.kind === 'task' && block.at === 'start' && block.id) {
      openTask = { id: block.id, number: String(block.number ?? '').trim() };
      // Aliased under the task's own declared number too, not just its internal id: a task's number/path
      // is the one identifier every reader outside this file already has (it's the same string `answers`
      // is keyed by), where the internal id is otherwise never exposed past this function. This is the
      // task's WHOLE box, prompt and answer together — see `boxes`'s own doc comment — which is what lets a
      // reader that also has the answer's own box (`answers[number].box`) carve the prompt-only portion out
      // by itself: the top slice down to where the answer box begins.
      const taskBox = boxes[block.id];
      if (openTask.number && taskBox && !boxes[openTask.number]) boxes[openTask.number] = taskBox;
      continue;
    }
    if (block.kind === 'end' && block.id && taskIds.has(block.id)) {
      if (openTask?.id === block.id) openTask = undefined;
      continue;
    }

    // A GAP is a POINT mark with no start/end pairing and no id/box of its own (see `AnswerRegion.gapBoxes`
    // and `gap`'s own comment in exam.typ) — checked before the shared `at !== 'start'` gate below, which
    // exists for the id-keyed blocks everything else in this pass looks up in `boxes`.
    if (block.kind === 'gap' && openTask && block.pos && typeof block.index === 'number') {
      if (typeof block.width === 'number' && typeof block.height === 'number') {
        const marks = gapMarksByTask.get(openTask.number) ?? [];
        marks.push({
          index: block.index,
          box: { page: block.pos.page, x: block.pos.x, y: block.pos.y, width: block.width, height: block.height },
        });
        gapMarksByTask.set(openTask.number, marks);
      }
      continue;
    }

    if (!openTask || !block.id || block.at !== 'start') continue;

    // A labelling figure inside a task's body carries that task's answer fields with it, whatever answer
    // region the task ALSO declared — the numbered boxes are what the leader lines point at, so they are
    // where the answer belongs (see `AnswerRegion.calloutBoxes` and the note in `figure-block`).
    //
    // `region.box` ALSO gets set here, to the figure-block's own box — picture and callouts together, but
    // not whatever prompt text the task prints above it — the same "answer's own box, not the task's whole
    // box" every other inline widget already gets from `gradias-track`. A submission's answer bbx
    // (`answerRect` in student-exam.ts) reads this directly; the online overlay never does (calloutBoxes
    // routes through its own early return in student-exam/page.tsx before `region.box` is ever read there).
    if (block.kind === 'figure' && calloutBoxesByFigure[block.id]) {
      const region = answers[openTask.number];
      if (region && !region.calloutBoxes) {
        region.calloutBoxes = calloutBoxesByFigure[block.id];
        if (!region.box) region.box = boxes[block.id];
      }
      continue;
    }

    if (!INLINE_ANSWER_WIDGET_KINDS.has(block.kind)) continue;
    const region = answers[openTask.number];
    const widgetBox = boxes[block.id];
    if (!region || region.answerType !== 'inline' || !widgetBox) continue;

    // The FIRST recognisable widget in the body is the task's answer; a second one is something else on
    // the page (a #grid-paper used as an illustration next to a checklist), and its rows do not belong here.
    if (region.box) continue;
    region.box = widgetBox;

    if (block.kind === 'mc' && mcOptionRows[block.id]) region.optionRows = mcOptionRows[block.id];
    if (block.kind === 'match' && matchRowsByWidget[block.id]) region.matchRows = matchRowsByWidget[block.id];
    if (block.kind === 'prompt-list' && promptRowsByWidget[block.id]) region.promptRows = promptRowsByWidget[block.id];
  }

  for (const [number, marks] of gapMarksByTask) {
    const region = answers[number];
    if (region && region.answerType === 'inline') {
      region.gapBoxes = marks
        .slice()
        .sort((a, b) => a.index - b.index)
        .map(mark => mark.box);
    }
  }

  return {
    pageWidthPt: examHeader?.pageWidthPt ?? 0,
    pageHeightPt: examHeader?.pageHeightPt ?? 0,
    boxes,
    answers,
  };
}

export interface CompileProblem {
  severity: 'error' | 'warning';
  message: string;
  taskNumber?: string;
}

/** A node in the shape `buildExamTasksFromRecursiveStructure` consumes. */
export interface ExamTaskNode {
  _id?: string;
  taskNumber: string;
  description: string;
  taskType: string;
  maxPoints?: number;
  ownMaxPoints?: number;
  /** Ids into the sibling `additionalInformation` pool `assembleTaskNodes` returns — see its `tasks` field. */
  additionalInfoIds?: string[];
  /**
   * Figures placed directly inside this task's own body, no `#material` wrapper — the RAW `<gradias>` ids
   * (not the generated-image ids `descriptionBoundingBoxes` below maps them to). Kept alongside
   * `additionalInfoIds` rather than folded into it because these were never part of the shared material
   * pool: `layout.boxes` (`computeBoundingBoxes`) still tracks one of these the same way it tracks any
   * other figure/material/task box, which is what lets the student-exam page offer a drawable surface over
   * it too (see `materialSlots` in `page.tsx`) using the exact same page-crop machinery as a real material,
   * with no separate rendering path needed.
   */
  inlineFigureIds?: string[];
  /** Image boxes for figures placed directly inside this task's own body (no `#material` wrapper). */
  descriptionBoundingBoxes?: BoundingBox[];
  subtasks: ExamTaskNode[];
}

/** One `#material(...)` block, exam-wide — what `Exam.additionalInformation` is built from. */
export interface AssembledAdditionalInfo {
  id: string;
  content: string;
  /** Set when a figure was generated for this material — see `figureAssets` in `assembleTaskNodes`. */
  boundingBoxes?: BoundingBox[];
}

const TASK_TYPES = new Set(Object.values(TaskType) as string[]);

const POINTS_EPSILON = 0.001;

// ─── validation ─────────────────────────────────────────────────────────────────────────────────────

/**
 * What a successful compile does not catch. A document can render perfectly and still be wrong in ways
 * that only surface later: a taskType no behaviour exists for, two tasks claiming one number, a parent
 * whose points contradict its children — which blocks structure confirmation outright (see
 * findPointInconsistencies, reached from confirm-structure.ts).
 *
 * `layout` is `computeBoundingBoxes(blocks)` — the caller's, not recomputed here — because checking an
 * `inline` task's answer needs its nesting-derived association (see that function's fourth pass), and
 * that is the one place which already does the walk.
 */
export function validateBlocks(blocks: GradiasBlock[], layout: ExamLayout): CompileProblem[] {
  const problems: CompileProblem[] = [];
  const exam = blocks.find(block => block.kind === 'exam' && block.at === 'start');
  const tasks = blocks.filter(block => block.kind === 'task' && block.at === 'start');

  if (!exam) problems.push({ severity: 'error', message: 'no exam block — is `#show: exam.with(...)` missing?' });
  if (tasks.length === 0) problems.push({ severity: 'error', message: 'the exam contains no tasks' });

  const byNumber = new Map<string, GradiasBlock>();
  for (const task of tasks) {
    const number = (task.number ?? '').trim();
    if (!number) {
      problems.push({ severity: 'error', message: `task "${task.id}" has no number` });
      continue;
    }
    if (byNumber.has(number)) {
      problems.push({ severity: 'error', message: `duplicate task number "${number}"`, taskNumber: number });
      continue;
    }
    byNumber.set(number, task);

    if (!TASK_TYPES.has(String(task.taskType))) {
      problems.push({
        severity: 'error',
        message: `unknown taskType "${task.taskType}" — must be a TaskType enum value`,
        taskNumber: number,
      });
    }

    const parentNumber = parentOf(number);
    if (parentNumber && !byNumber.has(parentNumber)) {
      problems.push({
        severity: 'error',
        message: `subtask "${number}" has no parent "${parentNumber}" (a parent must appear before its subtasks)`,
        taskNumber: number,
      });
    }

    if (task.taskType !== TaskType.TASK_FRAME && !(Number(task.points) > 0)) {
      problems.push({ severity: 'warning', message: `task "${number}" carries no points`, taskNumber: number });
    }
    if (!(task.text ?? '').trim()) {
      problems.push({ severity: 'error', message: `task "${number}" has an empty prompt`, taskNumber: number });
    }
  }

  // A material linked to a task number nothing declares is silently unlinked rather than a compile
  // failure (the figure is still printed, and grading falls back to treating it as exam-wide shared
  // material — see resolveAdditionalInfoForTask) — but it is almost always a typo the repair loop should
  // still be told about.
  for (const block of blocks) {
    if (block.kind !== 'material' || block.at !== 'start') continue;
    for (const taskNumber of block.tasks ?? []) {
      if (!byNumber.has(taskNumber.trim())) {
        problems.push({
          severity: 'warning',
          message: `material "${block.id}" is linked to task "${taskNumber}", which does not exist`,
        });
      }
    }
  }

  // EVERY ANSWERABLE TASK NEEDS SOMEWHERE TO ANSWER. This is checked here and not in the template because
  // only this layer sees the whole stream: whether a task is answerable depends on whether any other task
  // is its child, and `#task` is called once per task with no knowledge of the ones after it.
  //
  // An error, not a warning, and deliberately so: a task with nowhere to write is broken output — on paper
  // the student has no room, online there is nothing to place an input against. Failing here is what puts
  // it in front of the repair loop, which is the only thing that reliably fixes it.
  const answerTypeByTask = new Map<string, string>();
  for (const block of blocks) {
    if (block.kind !== 'answer') continue;
    const taskNumber = String(block.task ?? '').trim();
    if (taskNumber) answerTypeByTask.set(taskNumber, String(block.answerType ?? ''));
  }

  for (const [number, task] of byNumber) {
    const hasChildren = [...byNumber.values()].some(other => parentOf(other.number ?? '') === number);

    // A TASK_FRAME with no children is not "purely structural" — it is a leaf, and `needsAnswer` (see
    // build-exam-tasks.ts) makes leaf-TASK_FRAME content permanently ungraded: real prose asking a real
    // question, wearing a type that guarantees nobody ever checks the answer. Any points on it are
    // unearnable by construction, which is a worse defect than a missing answer region — so this is
    // checked BEFORE the frame gets its free pass below, not folded into it.
    if (task.taskType === TaskType.TASK_FRAME && !hasChildren) {
      problems.push({
        severity: 'error',
        message:
          `task "${number}" is TASK_FRAME but has no subtasks — TASK_FRAME only groups/introduces other ` +
          `tasks and is never itself graded (needsAnswer is always false for it), so this task's ` +
          `${Number(task.points) || 0} points can never be earned. Either add subtasks under "${number}" ` +
          `or give it the taskType the question actually is (its content decides which — see the ` +
          `catalogue), with a real answer region`,
        taskNumber: number,
      });
      continue;
    }

    // A frame with real children, and a parent whose answer lives entirely in its children, are both
    // legitimately un-answered THEMSELVES.
    if (task.taskType === TaskType.TASK_FRAME || hasChildren) continue;

    const answerType = answerTypeByTask.get(number);
    if (!answerType) {
      problems.push({
        severity: 'error',
        message:
          `task "${number}" declares no answer region — pass answer: "lines" | "numbered-lines" | "grid" | ` +
          `"blank" | "inline" to #task (see ANSWER-TYPES in exam.typ for which to use)`,
        taskNumber: number,
      });
    } else if (answerType === 'none' && !layout.answers?.[number]?.calloutBoxes?.length) {
      // A LABELLING FIGURE IS THE EXCEPTION TESTED ABOVE: `#figure-block(callouts: ...)` draws one
      // numbered box per structure, each at the end of a leader line pointing at what it names, and those
      // boxes are writing space — on paper and, via `AnswerRegion.calloutBoxes`, online. A task built
      // around one has a real answer region; it just isn't a region this task declared, so "none" is the
      // correct declaration rather than a missing one, and printing lines under it as well would give the
      // student a second place to write the same list.
      problems.push({
        severity: 'error',
        message:
          `task "${number}" declares answer: "none" but has no subtasks to answer it — a task students ` +
          `actually answer needs a real region, or "inline" if the answer is written in its own body`,
        taskNumber: number,
      });
    } else if (
      answerType === 'inline' &&
      !layout.answers?.[number]?.box &&
      !layout.answers?.[number]?.gapBoxes?.length
    ) {
      // `inline` has no box of its own — the ONLY way the online exam page can place a widget over it is
      // if a #mc/#match-columns/#grid-paper/#prompt-list/#gap-text is nested somewhere inside this task's
      // own body (resolved as `box`), or the body is scattered with bare #gap calls from an OLDER source
      // with no #gap-text wrapper (resolved as `gapBoxes`, which has no single box to nest — see its own
      // comment) — what `computeBoundingBoxes`'s nesting pass looks for (no naming convention involved —
      // see INLINE_ANSWER_WIDGET_KINDS). Without one, the compiled document is otherwise perfectly fine
      // (this is not a print defect — it prints correctly either way) but the online page has nowhere to
      // put the input, so it is caught here rather than surfacing later as a silently unplaceable answer.
      problems.push({
        severity: 'error',
        message:
          `task "${number}" declares answer: "inline" but no #mc(...), #match-columns(...), ` +
          `#grid-paper(...), #prompt-list(...), #gap-text(...) or a bare #gap(...) was found inside its ` +
          `body — the online exam page has nowhere to place this task's answer input`,
        taskNumber: number,
      });
    }
  }

  // A parent's declared points are its whole subtree, so they must equal its children's sum plus
  // whatever the parent itself answers.
  for (const [number, task] of byNumber) {
    const children = [...byNumber.values()].filter(other => parentOf(other.number ?? '') === number);
    if (children.length === 0) continue;
    const childSum = sumPoints(children);
    const declared = Number(task.points) || 0;
    const own = Number(task.ownPoints) || 0;
    if (declared > 0 && Math.abs(declared - childSum - own) > POINTS_EPSILON) {
      problems.push({
        severity: 'error',
        message:
          `task "${number}" declares ${declared} points but its subtasks sum to ${childSum}` +
          `${own ? ` plus ${own} own points` : ''} — structure confirmation rejects this`,
        taskNumber: number,
      });
    }
  }

  const rootSum = sumPoints([...byNumber.values()].filter(task => !parentOf(task.number ?? '')));
  const declaredTotal = Number(exam?.declaredTotalPoints) || 0;
  if (declaredTotal > 0 && Math.abs(rootSum - declaredTotal) > POINTS_EPSILON) {
    problems.push({
      severity: 'error',
      message: `the header declares ${declaredTotal} total points but the tasks sum to ${rootSum}`,
    });
  }

  return problems;
}

const parentOf = (number: string): string => number.split('.').slice(0, -1).join('.');

const sumPoints = (tasks: GradiasBlock[]): number => tasks.reduce((sum, task) => sum + (Number(task.points) || 0), 0);

// ─── structure → task tree ──────────────────────────────────────────────────────────────────────────

/** typst's own figure supplement per language, so a rewritten reference matches what is printed. */
const DEFAULT_SUPPLEMENT = 'Figure';
const FIGURE_SUPPLEMENT: Record<string, string> = {
  de: 'Abbildung',
  en: DEFAULT_SUPPLEMENT,
  fr: 'Figure',
  es: 'Figura',
};

/**
 * Composes each task's description and nests the flat stream by task number.
 *
 * `existingIdsByNumber` carries a task's `_id` across recompiles, and it has to: the master solution and
 * grading scheme live INSIDE the task, so minting a fresh id on every save would drop the teacher's
 * solutions on the floor. Task numbers are the identity that survives (they are also what student
 * answers bind to — SubmissionTask.taskNumber), which is why a renumber is reported by the caller rather
 * than quietly absorbed here.
 *
 * Also collects every `#material(...)` block into the exam-wide additional-information pool, linking it
 * onto whichever tasks its `tasks:` argument names — a material with no task using it, or that only lives
 * inside a task's own body, resolves to that task via `resolveAdditionalInfoForTask`'s "no specific task"
 * fallback (see additional-info-context.ts), but a material meant for SOME tasks and not others is only
 * seen by the right ones if it is linked here.
 */
export function assembleTaskNodes(
  blocks: GradiasBlock[],
  existingIdsByNumber = new Map<string, string>(),
  figureAssets: { materialId: string; fileId: string }[] = [],
  /**
   * Plot materials (`Exam.plotAssets`) never have a `figureAssets` entry — they get their own image box
   * from a compiled-page crop instead, added later in `compile-exam.ts`'s `attachRenderedFigureCrops`.
   * Passed in only so `droppedFigureIds` below doesn't misreport an expected plot omission as a real one;
   * nothing here reaches into `plotAssets` itself, keeping this module's own no-filesystem/no-DB contract.
   */
  plotMaterialIds: ReadonlySet<string> = new Set(),
): {
  tasks: ExamTaskNode[];
  additionalInformation: AssembledAdditionalInfo[];
  /** Figure ids referenced (as a shared material or inline in a task body) that have NEITHER a
   * `figureAssets` entry NOR are a known plot material — an asset that should exist but doesn't (e.g. its
   * generation failed at creation time). The caller logs these; this module stays side-effect free. */
  droppedFigureIds: string[];
} {
  const fileIdByMaterial = new Map(figureAssets.map(asset => [asset.materialId, asset.fileId]));
  const droppedFigureIds: string[] = [];
  const noteIfDropped = (figureId: string): void => {
    if (!fileIdByMaterial.has(figureId) && !plotMaterialIds.has(figureId)) droppedFigureIds.push(figureId);
  };
  /** Geometry-less image box — same shape manual image uploads use, see `update-exam-task-content.ts`. */
  const boxFor = (fileId: string) =>
    new BoundingBox({
      contentType: 'image',
      imageId: fileId,
      approved: true,
      confidence: 1,
      isHandwritten: false,
      ocrExtractedText: '',
    });
  const exam = blocks.find(block => block.kind === 'exam' && block.at === 'start');
  const supplement = FIGURE_SUPPLEMENT[exam?.lang ?? 'de'] ?? DEFAULT_SUPPLEMENT;

  // TODO (agreed, not built): a LABEL_IMAGE figure's `callouts` metadata already carries the
  // number→label pairing the template printed — "1 = rechter Vorhof". That IS the master solution for
  // that task, in the exact format label-image.ts expects (`1 -> rechter Vorhof;`), and it is the only
  // solution in the system that needs no model call at all because the exam was authored knowing it.
  // Writing it onto the task here would also mean `msGeneration` never targets these tasks (a task with
  // master-solution content is skipped by `hasMasterSolutionContent`). Left out of the first version so
  // creation and solution generation stay separable.

  // Figures are numbered in document order — the same order typst numbers them on the page.
  const figureLabels = new Map<string, string>();
  let figureIndex = 0;
  for (const block of blocks) {
    if (block.kind !== 'figure' || block.at !== 'start') continue;
    figureIndex += 1;
    if (block.ref) figureLabels.set(block.ref, `${supplement} ${figureIndex}`);
  }

  const taskIds = new Set(
    blocks.filter(block => block.kind === 'task' && block.at === 'start').map(block => block.id ?? ''),
  );

  const additionalInformation: AssembledAdditionalInfo[] = [];
  const additionalInfoIdsByTask = new Map<string, string[]>();
  for (const block of blocks) {
    if (block.kind !== 'material' || block.at !== 'start' || !block.id) continue;
    const fileId = fileIdByMaterial.get(block.id);
    additionalInformation.push({
      id: block.id,
      content: (block.text ?? '').trim(),
      boundingBoxes: fileId ? [boxFor(fileId)] : [],
    });
    for (const rawNumber of block.tasks ?? []) {
      const taskNumber = rawNumber.trim();
      if (!taskNumber) continue;
      const linked = additionalInfoIdsByTask.get(taskNumber) ?? [];
      linked.push(block.id);
      additionalInfoIdsByTask.set(taskNumber, linked);
    }
  }

  // A figure whose id is already in the material pool is a shared `#material(...)` figure, handled
  // above; anything else with an open task around it was placed directly inside that task's own body.
  const materialIds = new Set(additionalInformation.map(material => material.id));

  interface Draft extends ExamTaskNode {
    parts: string[];
    figureIds: string[];
    subtasks: Draft[];
  }

  const drafts = new Map<string, Draft>();
  const order: string[] = [];
  let open: Draft | undefined = undefined;

  for (const block of blocks) {
    if (block.kind === 'task' && block.at === 'start') {
      const number = (block.number ?? '').trim();
      if (!number || drafts.has(number)) continue;
      const draft: Draft = {
        _id: existingIdsByNumber.get(number),
        taskNumber: number,
        description: '',
        taskType: TASK_TYPES.has(String(block.taskType)) ? String(block.taskType) : TaskType.DEFAULT,
        maxPoints: Number(block.points) > 0 ? Number(block.points) : undefined,
        ownMaxPoints: Number(block.ownPoints) > 0 ? Number(block.ownPoints) : undefined,
        parts: [rewriteRefs(block.text ?? '', figureLabels)],
        figureIds: [],
        subtasks: [],
      };
      drafts.set(number, draft);
      order.push(number);
      open = draft;
      continue;
    }

    // A block between a task's start and its own end belongs to that task. Multiple-choice options are
    // appended as the canonical checklist the OCR and grading agent expect, so a created exam's
    // description has the same shape a scan of it would have produced.
    if (block.kind === 'mc' && open && Array.isArray(block.options)) {
      open.parts.push(block.options.join('\n'));
      continue;
    }

    if (block.kind === 'figure' && block.at === 'start' && block.id && open && !materialIds.has(block.id)) {
      open.figureIds.push(block.id);
      continue;
    }

    // Only the task's OWN end closes it — a figure or material inside the body ends first.
    if (block.kind === 'end' && open && block.id && taskIds.has(block.id)) open = undefined;
  }

  for (const number of order) {
    const draft = drafts.get(number);
    const parent = drafts.get(parentOf(number));
    if (draft && parent) parent.subtasks.push(draft);
  }

  const toNode = (draft: Draft): ExamTaskNode => {
    // Look up linked materials using the full path before stripping the prefix
    const additionalInfoIds = additionalInfoIdsByTask.get(draft.taskNumber) ?? [];

    // Transform "1.a" -> "a", "1.a.i" -> "i", while leaving top-level "1" unchanged
    const relativeTaskNumber = draft.taskNumber.includes('.')
      ? draft.taskNumber.slice(draft.taskNumber.lastIndexOf('.') + 1)
      : draft.taskNumber;

    // Every id in `figureIds` is a real figure/plot by construction (unlike a `#material` block, which
    // can be pure text) — so a miss here is always worth flagging, never a legitimate text-only case.
    for (const figureId of draft.figureIds) if (!fileIdByMaterial.has(figureId)) noteIfDropped(figureId);

    const descriptionBoundingBoxes = draft.figureIds
      .map(figureId => fileIdByMaterial.get(figureId))
      .filter((fileId): fileId is string => Boolean(fileId))
      .map(boxFor);

    return {
      _id: draft._id,
      taskNumber: relativeTaskNumber,
      description: draft.parts.filter(part => part.trim().length > 0).join('\n\n'),
      taskType: draft.taskType,
      maxPoints: draft.maxPoints,
      ownMaxPoints: draft.ownMaxPoints,
      additionalInfoIds,
      inlineFigureIds: draft.figureIds,
      descriptionBoundingBoxes,
      subtasks: draft.subtasks.map(toNode),
    };
  };

  const tasks = order
    .filter(number => !drafts.has(parentOf(number)))
    .flatMap(number => {
      const draft = drafts.get(number);
      return draft ? [toNode(draft)] : [];
    });

  return { tasks, additionalInformation, droppedFigureIds };
}

/** `@abb1` in a prompt becomes the label the page actually prints ("Abbildung 1"). */
export function rewriteRefs(text: string, figureLabels: Map<string, string>): string {
  let result = text;
  for (const [ref, label] of figureLabels) result = result.split(`@${ref}`).join(label);
  // Anything still carrying an `@` referred to something that is not a figure; the teacher should not
  // see the raw label syntax either way.
  // eslint-disable-next-line require-unicode-regexp -- `v` needs an es2024 target; the project is es2020
  return result.replace(/@(?<label>[\w-]+)/gu, '$<label>');
}
